import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  FadeLayer, SyncFadeOverlay, Container, FadeInContainer, WaitingScreen, ScreenCode, StatusText,
  ConnectionIndicator, StatusDot, DisconnectedOverlay, DisconnectedIcon, DisconnectedTitle,
  DisconnectedMessage, ReconnectButton, ReconnectTimer, ErrorScreen, LoadingFallback,
} from './ScreenClient.styles';
import { connectScreenSocket } from '../services/screenSocketOwner';
import { InterludeOverlay } from '../components/ScreenManager';
import { MediaPlayer, AudioPlayer } from '../components/Media';
import ExitFullscreenButton from '../components/ScreenClient/ExitFullscreenButton';
import lazyWithReload from '../utils/lazyWithReload';
import ModuleRoute from './ModuleRoute';
import ScreenSelector from './ScreenSelector';
import { VALID_SCREEN_CODES, VALID_IPAD_CODES, VALID_DEVICE_CODES } from './screenCodes';
import { getBackendUrl } from '../config/api';

// Lazy load screen components
const SystemicMapScreen = lazyWithReload(() => import('./SystemicMapScreen'));
const SystemicMapV2Screen = lazyWithReload(() => import('./systemic-v2/SummaryMapScreen'));
const SystemicReviewWallScreen = lazyWithReload(() => import('./systemic-v2/ReviewWallScreen'));
const PainPointsCollectScreen = lazyWithReload(() => import('./PainPointsCollectScreen'));
const RagSourceMapScreen = lazyWithReload(() => import('./RagSourceMapScreen'));
const RagSourceMapV2Screen = lazyWithReload(() => import('./RagSourceMapV2Screen'));
const SWOTScreen = lazyWithReload(() => import('./SWOTScreen'));
const CentralTable = lazyWithReload(() => import('./CentralTable'));
const TestKuutio = lazyWithReload(() => import('./TestKuutio'));
const KuutioWelcomeWall = lazyWithReload(() => import('./KuutioWelcomeWall'));
const WelcomeScreen = lazyWithReload(() => import('./WelcomeScreen'));
const TimerWall = lazyWithReload(() => import('./TimerWall'));
const LensesScreen = lazyWithReload(() => import('./LensesScreen'));
const PublicWorkshopReport = lazyWithReload(() => import('./PublicWorkshopReport'));

/* Two-layer fade: outgoing content fades to black, incoming eases in after.
   Layers are absolutely positioned so both can render during the transition. */
/**
 * ControlledSlides subscribes to presentation-slide-changed only when it has an
 * instanceId; without one it renders its initial slide and never advances. The
 * facilitator's picker writes instanceId into mediaContent, but a config
 * authored anywhere else (setup script, hand-edited tila) can omit it — and the
 * deck then ticks server-side while every wall sits frozen. Stamp it in wherever
 * a pushed or restored config becomes mediaContent.
 */
function withInstanceId(mediaContent, instanceId) {
  if (!mediaContent || mediaContent.instanceId || !instanceId) return mediaContent;
  return { ...mediaContent, instanceId };
}

/** Views rendered through MediaPlayer rather than a Kuutio view component.
 *  Both the screen-command handler and the saved-state restore must map these
 *  the same way, or a reconnecting screen renders nothing. */
const MEDIA_VIEW_IDS = ['slides', 'video', 'image', 'blank', 'controlled-slides', 'screen/controlled-slides'];

/** Flow Manager controlled presentations arrive as 'slides'. */
function normaliseMediaType(viewId) {
  return (viewId === 'slides' || viewId === 'screen/controlled-slides')
    ? 'controlled-slides'
    : viewId;
}

// Modules with versions show the one the instance's chain uses (#685)
const ImpactPathModule = (props) => <ModuleRoute family="impact_path" {...props} />;
const PivotPointsModule = (props) => <ModuleRoute family="pivot_points" {...props} />;
const SolutionsModule = (props) => <ModuleRoute family="solutions" {...props} />;

const VIEW_COMPONENTS = {
  'impact-path': ImpactPathModule,
  'impactpath': ImpactPathModule,
  'systemic-map': SystemicMapScreen,
  'systemicmap': SystemicMapScreen,
  // Systeemikartta v2: on the wall from Juurisyyt on (specs/systemic-map-v2-room-review.md §0)
  'systemic-map-v2': SystemicMapV2Screen,
  'systemic-map-v2-review': SystemicReviewWallScreen, // the card under discussion
  'future-reimagined': lazyWithReload(() => import('./future-reimagined/FutureWall')), // Kuviteltu tulevaisuus (#946)
  'future-v2': lazyWithReload(() => import('../modules/future_reimagined_v2/FutureWallV2')), // Tulevaisuustestaus v2 (#1474)
  'statistics-view': lazyWithReload(() => import('../modules/statistics_view_v1/StatisticsWallView')), // Tilastot seinällä (#1562)
  'solutions-v2': lazyWithReload(() => import('./systemic-v2/SolutionsWall')), // Ratkaisut v2: the room's solutions and their scores (#1312)
  'solutions-v4': lazyWithReload(() => import('../modules/solutions_v4/SolutionsWallV4')), // Ratkaisut v4: the room's solutions by vipupiste (#1474)
  'pain-points': PainPointsCollectScreen,
  'painpoints': PainPointsCollectScreen,
  'pivot-points': PivotPointsModule,
  'pivotpoints': PivotPointsModule,
  // Retired (#687): walls still set to these show Esteet ja mahdollisuudet
  'pivot-static': LensesScreen,
  'pivot-manual': LensesScreen,
  'rag-source-map': RagSourceMapScreen,
  'ragsourcemap': RagSourceMapScreen,
  'rag-source-map-v2': RagSourceMapV2Screen,
  'ragsourcemapv2': RagSourceMapV2Screen,
  'mood': SWOTScreen,
  'swot': SWOTScreen,
  // Retired (#1487): a wall still set to postit, postit-ai or weather shows the waiting screen
  'table': CentralTable,
  'waiting': TestKuutio,
  // Day 2 + results (workshop menu views)
  'lenses': LensesScreen,
  'solutions': SolutionsModule,
  'report': PublicWorkshopReport,
  // Wall display screens
  'welcome': WelcomeScreen,      // Secondary screens - welcome text
  'kuutio': KuutioWelcomeWall,   // Primary screens - rotating cube video
  'timer': TimerWall,
  // Tilannekuva — live workshop dashboard (specs/workshop-dashboard.md)
  'dashboard': lazyWithReload(() => import('../dashboard/DashboardScreen')),
  'blank': null, // Explicit blank screen (shows waiting screen)
};




/**
 * ScreenClient - Client component for managed screens
 *
 * Route: /screen/:code
 * Example: /screen/A1?instance=uuid
 *
 * Registers with backend, listens for commands, displays content,
 * and shows interludes when triggered.
 */
const ScreenClient = () => {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const instanceId = searchParams.get('instance') || searchParams.get('kid') || searchParams.get('instanceId');
  const initialView = searchParams.get('view'); // Support ?view=kuutio for test mode
  const isPreviewMode = searchParams.get('preview') === 'true'; // Preview mode skips socket entirely
  const isVirtualMode = searchParams.get('virtual') === 'true'; // Virtual mode connects but doesn't register
  const isLite = searchParams.get('lite') === '1'; // Lo-res previews (play view) — real screens never set this
  const upperCode = code?.toUpperCase();

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [wasConnected, setWasConnected] = useState(false); // Track if we had connection before
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [reconnectCountdown, setReconnectCountdown] = useState(0);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [currentView, setCurrentView] = useState(initialView || null);
  const [syncFade, setSyncFade] = useState(false);
  const [viewParams, setViewParams] = useState({});
  const [mediaContent, setMediaContent] = useState(() => {
    // Restore mediaContent from localStorage on mount (for reconnect scenarios)
    try {
      const saved = localStorage.getItem(`kuutio-screen-${code?.toUpperCase()}-media`);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }); // For slides/video/image content
  const [interlude, setInterlude] = useState(null);
  const [error, setError] = useState(null);
  const [instanceName, setInstanceName] = useState(null);

  // Fade-out/in on content change: the outgoing view is snapshotted and
  // rendered as a fading ghost layer while the new content eases in
  const contentKey = `${currentView || 'waiting'}:${mediaContent?.imageUrl || mediaContent?.videoUrl || ''}`;
  const [prevLayer, setPrevLayer] = useState(null);
  const layerSnapshotRef = useRef(null);
  useEffect(() => {
    const snapshot = layerSnapshotRef.current;
    layerSnapshotRef.current = { key: contentKey, view: currentView, mediaContent, viewParams };
    if (snapshot && snapshot.key !== contentKey && snapshot.view) {
      setPrevLayer(snapshot);
      const t = setTimeout(() => setPrevLayer(null), 500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [contentKey, currentView, mediaContent, viewParams]);

  // Refs
  const socketRef = useRef(null);
  const heartbeatRef = useRef(null);
  const pendingTickRef = useRef(null); // prepare→reveal in-flight content
  const clockOffsetRef = useRef(0); // serverTime − clientTime (ms)
  const [clockOffset, setClockOffset] = useState(0);
  const reconnectTimerRef = useRef(null);
  const videoPlayerRef = useRef(null); // For video playback control

  // Validate device code (screens A1-D2 or iPads P1-P10)
  const isValidCode = VALID_DEVICE_CODES.includes(upperCode);

  // Handle screen commands from facilitator
  const handleScreenCommand = useCallback((data) => {
    // Only respond to commands targeted at this screen
    if (data.targetScreen && data.targetScreen !== upperCode) {
      return; // Not for us
    }

    console.log(`[ScreenClient ${upperCode}] Received command:`, data);

    // Remote reload: pick up a freshly deployed bundle without touching the
    // TV. State (view, tila, audio) restores automatically on rejoin.
    if (data.command === 'reload') {
      window.location.reload();
      return;
    }

    if (data.command === 'switch-view' && data.payload?.view) {
      const viewId = data.payload.view.replace(/^\//, ''); // Remove leading slash
      console.log(`[ScreenClient ${upperCode}] Switching to view: ${viewId}`);

      // Check if this is a media type (slides, video, image)
      if (viewId === 'media' && data.payload.viewParams) {
        // Media content - use MediaPlayer
        const mediaType = data.payload.viewParams.type;
        console.log(`[ScreenClient ${upperCode}] Setting media content: ${mediaType}`);
        setMediaContent(withInstanceId(data.payload.viewParams, instanceId));
        setCurrentView('media');
        setViewParams({});
      } else if (MEDIA_VIEW_IDS.includes(viewId)) {
        // Direct media type - construct media content.
        // Shares MEDIA_VIEW_IDS/normaliseMediaType with the saved-state restore
        // so the two paths cannot drift apart.
        const mediaType = normaliseMediaType(viewId);
        console.log(`[ScreenClient ${upperCode}] Direct media type: ${mediaType}`);
        setMediaContent({
          type: mediaType,
          instanceId, // Pass instanceId for controlled-slides socket events
          ...data.payload.params
        });
        setCurrentView('media');
        setViewParams({});
      } else {
        // Regular Kuutio view
        setMediaContent(null);
        setCurrentView(viewId);
        setViewParams(data.payload.params || {});
      }
    }

    // Handle media control commands (play, pause, seek, restart)
    if (data.command === 'media-control' && data.payload) {
      const { action, seekTo } = data.payload;
      console.log(`[ScreenClient ${upperCode}] Media control: ${action}`);

      if (videoPlayerRef.current) {
        switch (action) {
          case 'play':
            videoPlayerRef.current.play();
            break;
          case 'pause':
            videoPlayerRef.current.pause();
            break;
          case 'restart':
            videoPlayerRef.current.restart();
            break;
          case 'seek':
            if (typeof seekTo === 'number') {
              videoPlayerRef.current.seek(seekTo);
            }
            break;
        }
      }
    }
  }, [upperCode]);

  // Handle interlude events
  const handleInterludeShow = useCallback((data) => {
    console.log('[ScreenClient] Interlude show:', data);
    setInterlude({
      ...data.config,
      remaining: data.duration,
      duration: data.duration,
    });
  }, []);

  const handleInterludeTick = useCallback((data) => {
    setInterlude((prev) => prev ? { ...prev, remaining: data.remaining } : null);
  }, []);

  const handleInterludeComplete = useCallback(() => {
    console.log('[ScreenClient] Interlude complete');
    setInterlude(null);
  }, []);

  // Manual reconnect function
  const handleManualReconnect = useCallback(() => {
    console.log('[ScreenClient] Manual reconnect triggered');
    setIsReconnecting(true);
    setError(null);

    // Clear any pending timers
    if (reconnectTimerRef.current) {
      clearInterval(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    setReconnectCountdown(0);

    // Force reconnect by reloading the page (cleanest approach)
    window.location.reload();
  }, []);

  // Fetch instance name
  useEffect(() => {
    if (!instanceId) return;

    const socketUrl = getBackendUrl();
    fetch(`${socketUrl}/api/instances/${instanceId}`)
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (data?.name) setInstanceName(data.name);
      })
      .catch(() => {}); // Silently fail
  }, [instanceId]);

  // Connect to socket on mount
  useEffect(() => {
    if (!isValidCode || !instanceId) {
      // Don't set error immediately - wait for params to load
      return;
    }

    // Preview mode: just render the view without socket connection
    // This prevents modal previews from disconnecting the real screen
    // Preview ALWAYS trumps virtual mode
    if (isPreviewMode) {
      console.log(`[ScreenClient] Preview mode - skipping socket connection for ${upperCode}`);
      setCurrentView(initialView || 'waiting');
      // Designer previews pass media content in the URL since there is no socket
      const mediaParam = searchParams.get('media');
      if (mediaParam) {
        try {
          setMediaContent(JSON.parse(mediaParam));
        } catch (e) {
          console.warn('[ScreenClient] Invalid media param:', e.message);
        }
      }
      setIsConnected(true); // Fake connected state for rendering
      return;
    }

    // Clear any previous error
    setError(null);

    const modeLabel = isVirtualMode ? 'virtual' : 'real';
    console.log(`[ScreenClient] Connecting ${modeLabel} screen ${upperCode} for instance ${instanceId}`);

    // Connect to socket
    const socket = connectScreenSocket(instanceId, 'screen', upperCode);
    socketRef.current = socket;

    // Helper to register the screen (virtual screens also register to receive commands)
    const registerScreen = () => {
      // A hidden tab must not claim a real screen code: a forgotten laptop
      // tab waking from sleep would silently steal the code from the TV.
      // TVs are never hidden, so this defers only the problem case.
      if (document.hidden && !isVirtualMode) {
        console.log('[ScreenClient] Tab hidden — deferring registration until visible');
        const onVisible = () => {
          if (!document.hidden) {
            document.removeEventListener('visibilitychange', onVisible);
            if (socket.connected) registerScreen();
          }
        };
        document.addEventListener('visibilitychange', onVisible);
        return;
      }
      console.log(`[ScreenClient] Registering screen (virtual=${isVirtualMode})...`);
      socket.emit('screen-register', {
        code: upperCode,
        instanceId,
        virtual: isVirtualMode, // Mark virtual screens so backend can distinguish
        capabilities: {
          hasAudio: !isVirtualMode, // Virtual screens typically don't have audio
          resolution: `${window.innerWidth}x${window.innerHeight}`,
        },
      });
    };

    // Connection handlers
    const onConnect = () => {
      console.log('[ScreenClient] Connected');
      setIsConnected(true);
      setWasConnected(true);
      setReconnectAttempts(0);
      setReconnectCountdown(0);
      setIsReconnecting(false);
      setError(null);
      // Clear any pending reconnect timer
      if (reconnectTimerRef.current) {
        clearInterval(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      registerScreen();
    };

    // If socket is already connected (reusing from ScreenSelector), register immediately
    if (socket.connected) {
      console.log('[ScreenClient] Socket already connected, registering immediately');
      setIsConnected(true);
      setWasConnected(true);
      registerScreen();
    }

    const onDisconnect = (reason) => {
      console.log('[ScreenClient] Disconnected, reason:', reason);
      setIsConnected(false);

      // Auto-reconnect with exponential backoff (unless intentional disconnect)
      if (reason !== 'io client disconnect') {
        const attempt = reconnectAttempts + 1;
        const delay = Math.min(3000 * Math.pow(1.5, attempt - 1), 30000); // 3s, 4.5s, 6.75s... max 30s
        console.log(`[ScreenClient] Will auto-reconnect in ${delay}ms (attempt ${attempt})`);

        setReconnectAttempts(attempt);
        setReconnectCountdown(Math.ceil(delay / 1000));

        // Start countdown timer
        reconnectTimerRef.current = setInterval(() => {
          setReconnectCountdown(prev => {
            if (prev <= 1) {
              clearInterval(reconnectTimerRef.current);
              reconnectTimerRef.current = null;
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    };

    const onConnectError = (err) => {
      console.error('[ScreenClient] Connection error:', err);
      setError('Connection failed');
    };

    // Handle registration confirmation with saved state recovery
    const onRegistered = (data) => {
      console.log('[ScreenClient] Registered:', data);
      // Priority: 1) URL view param (if set), 2) DB saved state, 3) localStorage, 4) null (waiting)
      // URL view is trusted as source of truth if present - allows bookmarking/sharing
      if (initialView) {
        console.log(`📍 Using URL view param: ${initialView}`);
        // Already set from useState, no action needed
      } else if (data.savedState?.view) {
        console.log(`🔄 Restoring saved state from DB: ${data.savedState.view}`);
        const savedParams = data.savedState.params || {};
        const savedView = data.savedState.view;

        // Media-backed views must be normalised exactly as the screen-command
        // path does. Restoring 'slides' verbatim sets a view id that has no
        // renderer and leaves mediaContent null, so a screen that reconnects
        // shows "Waiting for content" while its correct state sits unused in
        // viewParams. Keep this in step with the screen-command handler.
        if (MEDIA_VIEW_IDS.includes(savedView)) {
          setMediaContent({
            type: normaliseMediaType(savedView),
            instanceId,
            ...savedParams,
          });
          setCurrentView('media');
          setViewParams({});
        } else if (savedView === 'media' && data.savedState.mediaContent) {
          setMediaContent(withInstanceId(data.savedState.mediaContent, instanceId));
          setCurrentView('media');
          setViewParams({});
        } else {
          setCurrentView(savedView);
          setViewParams(savedParams);
          if (data.savedState.mediaContent) setMediaContent(withInstanceId(data.savedState.mediaContent, instanceId));
        }
      }
      // Request current interlude state in case one is already active
      socket.emit('interlude-state-request', { instanceId });
      // Request current screen content state (for reconnect scenarios)
      socket.emit('screen-state-request', { instanceId, screenCode: upperCode });
    };

    // Handle screen state response (for reconnect scenarios)
    const onStateResponse = (data) => {
      console.log('[ScreenClient] Screen state response:', data);
      if (data.view) {
        console.log(`🔄 Syncing screen state: ${data.view}`);
        setCurrentView(data.view);
        if (data.params) setViewParams(data.params);
        if (data.mediaContent) {
          setMediaContent(withInstanceId(data.mediaContent, instanceId));
        }
      }
    };

    // Handle current interlude state (for screens joining mid-interlude)
    const onInterludeState = (data) => {
      console.log('[ScreenClient] Interlude state:', data);
      if (data.active) {
        setInterlude({
          ...data.config,
          remaining: data.remaining,
          duration: data.config?.duration,
          isPaused: data.isPaused,
        });
      } else {
        setInterlude(null);
      }
    };

    // Another physical display registered this code — stand down instead of
    // fighting over it (our heartbeats would flip its status back and forth)
    const onReplaced = ({ code }) => {
      console.warn(`[ScreenClient] Screen ${code} was taken over by another display`);
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      socket.disconnect(); // intentional: no auto-reconnect
      setError(`Toinen laite otti näytön ${code} käyttöön. Valitse tälle laitteelle toinen näyttökoodi.`);
    };

    // Prepare → ready → reveal: preload the next content hidden, ACK when it's
    // displayable (images: decoded; video: ready-to-stream), swap on reveal so
    // all 8 wall screens change in the same instant
    const onPrepare = async (data) => {
      const { tickId, view, params, mediaContent: mc } = data;
      pendingTickRef.current = { tickId, view, params, mediaContent: mc };
      try {
        if (mc?.imageUrl) {
          const img = new window.Image();
          img.src = mc.imageUrl;
          await Promise.race([
            img.decode().catch(() => {}),
            new Promise(r => setTimeout(r, 1800)),
          ]);
        } else if (mc?.videoUrl) {
          // "Ready" = ready to stream (enough buffered), not fully downloaded
          await new Promise(resolve => {
            const v = document.createElement('video');
            v.preload = 'auto';
            v.muted = true;
            v.src = mc.videoUrl;
            v.addEventListener('canplaythrough', resolve, { once: true });
            v.addEventListener('error', resolve, { once: true });
            setTimeout(resolve, 5000);
          });
        } else {
          // Kuutio views render from live data; a frame is enough warm-up
          await new Promise(r => setTimeout(r, 100));
        }
      } finally {
        if (pendingTickRef.current?.tickId === tickId) {
          socket.emit('screen-ready', { tickId, code: upperCode });
        }
      }
    };

    // Estimate offset to the server clock (Cristian's algorithm) — lets
    // scheduled reveals and video epochs agree across walls. NTP-style: the
    // lowest round-trip samples carry the least asymmetric queuing delay, so
    // their offset is the most trustworthy. Take more pings, keep only the
    // fastest, and median those — tightens agreement across walls on a jittery
    // (cloud) link where a plain median of all samples drifts screen-to-screen.
    const estimateClockOffset = async () => {
      const samples = [];
      for (let i = 0; i < 9; i++) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => {
          const t0 = Date.now();
          socket.timeout(1500).emit('clock-ping', null, (err, serverTime) => {
            if (!err && typeof serverTime === 'number') {
              const t1 = Date.now();
              const rtt = t1 - t0;
              samples.push({ offset: serverTime - (t0 + rtt / 2), rtt });
            }
            resolve();
          });
        });
      }
      if (samples.length) {
        samples.sort((a, b) => a.rtt - b.rtt);
        const best = samples.slice(0, Math.max(3, Math.ceil(samples.length * 0.4)));
        best.sort((a, b) => a.offset - b.offset);
        const median = best[Math.floor(best.length / 2)].offset;
        clockOffsetRef.current = median;
        setClockOffset(median);
      }
    };
    estimateClockOffset();
    // Re-estimate often: TV clocks drift apart over an idle hour, and a fresh
    // offset flows into the video sync (prop change restarts it). 90 s keeps
    // walls converged without noticeable ping traffic.
    const clockOffsetInterval = setInterval(estimateClockOffset, 90 * 1000);

    const onReveal = (data) => {
      const pending = pendingTickRef.current;
      if (!pending || pending.tickId !== data.tickId) return;
      pendingTickRef.current = null;

      const commit = () => {
        setCurrentView(pending.view);
        setViewParams(pending.params || {});
        // Video content carries the reveal instant as its playback epoch so
        // every wall computes the same "where should the video be now"
        const isVideoContent = pending.mediaContent
          && (pending.mediaContent.videoUrl || pending.mediaContent.type === 'tiled-360');
        const mc = isVideoContent && data.revealAt
          ? { ...pending.mediaContent, syncEpoch: data.revealAt }
          : pending.mediaContent;
        setMediaContent(withInstanceId(mc, instanceId) || null);
      };

      // Fire at the shared server-clock instant (converted to local time).
      // The room blinks as one: fade to black finishes just before the swap,
      // content switches under black, then all walls fade back in together.
      const FADE_LEAD = 650;
      const delay = data.revealAt
        ? (data.revealAt - clockOffsetRef.current) - Date.now()
        : 0;
      if (data.revealAt && delay > 0 && delay < 8000) {
        // On time: hit the shared instant.
        setTimeout(() => setSyncFade(true), Math.max(0, delay - FADE_LEAD));
        setTimeout(() => {
          commit();
          setTimeout(() => setSyncFade(false), 150);
        }, delay);
      } else if (data.revealAt) {
        // Reveal arrived after the shared instant (a slow browser or a latency
        // spike ate the lead), or the clock estimate is way off. We can't hit
        // the room instant, but blink locally anyway so this wall degrades to a
        // quick fade instead of a hard pop while the others fade.
        setSyncFade(true);
        setTimeout(() => {
          commit();
          setTimeout(() => setSyncFade(false), 150);
        }, FADE_LEAD);
      } else {
        commit();
      }
    };

    // The socket is shared: the cleanup removes exactly these handlers, and a
    // re-run (code or instance change) doesn't stack a second set (#1427)
    const listeners = {
      connect: onConnect, disconnect: onDisconnect, connect_error: onConnectError,
      'screen-registered': onRegistered, 'screen-state-response': onStateResponse,
      'interlude-state': onInterludeState, 'screen-replaced': onReplaced,
      'screen-prepare': onPrepare, 'screen-reveal': onReveal,
      'screen-command': handleScreenCommand, 'interlude-show': handleInterludeShow,
      'interlude-tick': handleInterludeTick, 'interlude-complete': handleInterludeComplete,
    };
    Object.entries(listeners).forEach(([event, handler]) => socket.on(event, handler));

    // Start heartbeat
    heartbeatRef.current = setInterval(() => {
      if (socket.connected) {
        socket.emit('screen-heartbeat', {
          code: upperCode,
          state: {
            view: currentView,
            timestamp: Date.now(),
          },
        });
      }
    }, 15000);

    // Cleanup
    return () => {
      console.log('[ScreenClient] Cleaning up');
      clearInterval(heartbeatRef.current);
      clearInterval(clockOffsetInterval);
      Object.entries(listeners).forEach(([event, handler]) => socket.off(event, handler));
      // Don't disconnect - socket is a shared singleton
    };
  }, [upperCode, instanceId, isValidCode, initialView, isPreviewMode, isVirtualMode, handleScreenCommand, handleInterludeShow, handleInterludeTick, handleInterludeComplete]);

  // Update URL when view changes (for bookmarking/sharing/refresh resilience)
  useEffect(() => {
    if (!currentView || !instanceId) return;

    // Update URL without triggering navigation
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', currentView);
    const newUrl = `${window.location.pathname}?${newParams.toString()}`;
    window.history.replaceState({}, '', newUrl);
  }, [currentView, instanceId, searchParams]);

  // Persist mediaContent to localStorage for reconnect scenarios
  useEffect(() => {
    if (!upperCode) return;
    const key = `kuutio-screen-${upperCode}-media`;
    if (mediaContent) {
      localStorage.setItem(key, JSON.stringify(mediaContent));
    } else {
      localStorage.removeItem(key);
    }
  }, [mediaContent, upperCode]);

  // Send state updates
  useEffect(() => {
    if (!socketRef.current?.connected) return;

    const interval = setInterval(() => {
      socketRef.current.emit('screen-state-update', {
        screenId: upperCode,
        view: currentView,
        stats: {},
        preview: {
          backgroundColor: '#0a0a0a',
          title: currentView || 'Waiting',
        },
      });
    }, 2000);

    return () => clearInterval(interval);
  }, [upperCode, currentView]);

  // If no code provided, show the screen selector
  if (!code) {
    return <ScreenSelector />;
  }

  // Render error state for invalid code
  if (!isValidCode) {
    return (
      <Container>
        <ErrorScreen>
          <h1>Invalid Device Code</h1>
          <p>Device code "{code}" is not valid.</p>
          <p>Screens: {VALID_SCREEN_CODES.join(', ')}</p>
          <p>iPads: {VALID_IPAD_CODES.join(', ')}</p>
        </ErrorScreen>
      </Container>
    );
  }

  if (error) {
    const isTakeover = error.includes('otti näytön');
    return (
      <Container>
        <ErrorScreen>
          <h1>{isTakeover ? 'Näyttö siirtyi toiselle laitteelle' : 'Connection Error'}</h1>
          <p>{error}</p>
          <p>Screen: {upperCode}</p>
          {isTakeover && (
            <button
              onClick={() => {
                document.documentElement.requestFullscreen?.().catch(() => {});
                window.location.reload();
              }}
              style={{
                marginTop: 24, padding: '16px 32px', fontSize: 20, borderRadius: 10,
                border: '1px solid #5ff7ff', background: 'rgba(95,247,255,0.1)',
                color: '#5ff7ff', cursor: 'pointer',
              }}
            >
              Ota näyttö {upperCode} takaisin
            </button>
          )}
        </ErrorScreen>
      </Container>
    );
  }

  // Get view component
  const ViewComponent = currentView ? VIEW_COMPONENTS[currentView] : null;

  return (
    <FadeInContainer>
      {/* Fullscreen is entered on the selection tap; this is the only way back
          on a touch-only wall display (Esc needs a keyboard). */}
      <ExitFullscreenButton />
      <Container>
        {/* Interlude overlay (highest priority) */}
        {interlude && (
          <InterludeOverlay
            type={interlude.type}
            title={interlude.title}
            subtitle={interlude.subtitle}
            duration={interlude.duration}
            remaining={interlude.remaining}
            showProgress={interlude.showProgress}
            showReturnTime={interlude.showReturnTime}
            theme={interlude.theme || 'dark'}
            isPaused={interlude.isPaused}
            onComplete={() => setInterlude(null)}
          />
        )}

        <SyncFadeOverlay $on={syncFade} />

        {/* Audio half of the screen: SFX + background channels. Only the
            audio-host screen (default A1) plays; virtual play-view previews
            stay silent unless the iframe URL carries audio=1. */}
        <AudioPlayer
          socket={socketRef.current}
          instanceId={instanceId}
          screenCode={upperCode}
          audioEnabled={!isPreviewMode && (!isVirtualMode || searchParams.get('audio') === '1')}
          clockOffset={clockOffset}
        />

        {/* Outgoing content fades to black while the new content eases in */}
        {!interlude && prevLayer && (
          <FadeLayer $out key={`out-${prevLayer.key}`}>
            {prevLayer.view === 'media' && prevLayer.mediaContent ? (
              <MediaPlayer content={prevLayer.mediaContent} screenCode={upperCode} />
            ) : VIEW_COMPONENTS[prevLayer.view] ? (
              <React.Suspense fallback={null}>
                {React.createElement(VIEW_COMPONENTS[prevLayer.view], {
                  ...(prevLayer.viewParams || {}),
                  instanceId,
                  isEmbedded: true,
                  screenMode: true,
                  disableNavigation: true,
                })}
              </React.Suspense>
            ) : null}
          </FadeLayer>
        )}

        {/* Main content */}
        {!interlude && (
          <FadeLayer key={contentKey}>
            {/* Media content (slides, video, image) */}
            {currentView === 'media' && mediaContent ? (
              <MediaPlayer
                ref={videoPlayerRef}
                content={mediaContent}
                screenCode={upperCode}
                clockOffset={clockOffset}
                socket={socketRef.current}
                instanceId={instanceId}
                lite={isLite}
              />
            ) : ViewComponent ? (
              <React.Suspense fallback={<LoadingFallback>Loading {currentView}...</LoadingFallback>}>
                <ViewComponent
                  {...viewParams}
                  instanceId={instanceId}
                  isEmbedded={true}
                  screenMode={true}
                  disableNavigation={true}
                  onViewChange={(newView) => {
                    // Allow switching between views in screenMode
                    console.log('📺 ScreenClient view change:', newView);
                    setCurrentView(newView);
                  }}
                />
              </React.Suspense>
            ) : (
              <WaitingScreen>
                <img
                  src="/assets/kuutio-logo.svg"
                  alt="Kuutio"
                  style={{ width: 180, height: 180, marginBottom: 32, opacity: 0.7 }}
                />
                <ScreenCode>{upperCode}</ScreenCode>
                <StatusText>
                  {isConnected ? 'Waiting for content...' : 'Connecting...'}
                </StatusText>
                {instanceName && (
                  <div style={{
                    marginTop: '24px',
                    padding: '8px 24px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid #222',
                    fontSize: '20px',
                    fontWeight: 600,
                    color: '#888',
                    letterSpacing: '-0.01em',
                  }}>
                    {instanceName}
                  </div>
                )}
              </WaitingScreen>
            )}
          </FadeLayer>
        )}

        {/* Disconnected overlay - show when we lost connection */}
        {wasConnected && !isConnected && (
          <DisconnectedOverlay>
            <DisconnectedIcon>⚡</DisconnectedIcon>
            <DisconnectedTitle>Yhteys katkesi</DisconnectedTitle>
            <DisconnectedMessage>
              {upperCode} • {reconnectAttempts > 0 ? `Yritys ${reconnectAttempts}` : 'Yhdistetään uudelleen...'}
            </DisconnectedMessage>
            {reconnectCountdown > 0 && (
              <ReconnectTimer>
                Automaattinen yhdistäminen {reconnectCountdown}s...
              </ReconnectTimer>
            )}
            <ReconnectButton onClick={handleManualReconnect} disabled={isReconnecting}>
              {isReconnecting ? 'Yhdistetään...' : 'Yhdistä nyt'}
            </ReconnectButton>
          </DisconnectedOverlay>
        )}

        {/* Connection indicator */}
        <ConnectionIndicator $connected={isConnected}>
          <StatusDot />
          {upperCode}
        </ConnectionIndicator>
      </Container>
    </FadeInContainer>
  );
};

export default ScreenClient;
